import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row } from '@/lib/supabase'

export type Vendor = Row<'vendors'> & { jobs: Array<{ job_id: string }> }

export const SERVICE_LABEL: Record<string, string> = {
  planner: 'Planner',
  venue: 'Venue',
  caterer: 'Caterer',
  florist: 'Florist',
  decor_hire: 'Décor and furniture hire',
  dj_music: 'DJ or band',
  videographer: 'Videographer',
  photographer: 'Photographer',
  content_creator: 'Content creator',
  bridal_wear: 'Bridal wear',
  hair_makeup: 'Hair and make-up',
  cake: 'Cake',
  officiant: 'Officiant',
  transport: 'Transport',
  other: 'Other',
}

const vendorKeys = { all: ['vendors'] as const }

export function useVendors() {
  return useQuery({
    queryKey: vendorKeys.all,
    queryFn: async (): Promise<Vendor[]> => {
      const { data, error } = await supabase.from('vendors').select('*, jobs:vendor_jobs ( job_id )').order('name')
      if (error) throw error
      return data as Vendor[]
    },
  })
}

export interface VendorInput {
  id?: string
  name: string
  service: string
  email: string | null
  phone: string | null
  rating: number | null
  preferred: boolean
  notes: string | null
  jobIds: string[]
}

/** Saves the vendor and makes its weddings match `jobIds`. */
export function useSaveVendor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, jobIds, ...fields }: VendorInput) => {
      let vendorId = id
      if (vendorId) {
        const { error } = await supabase.from('vendors').update(fields).eq('id', vendorId)
        if (error) throw error
      } else {
        const { data, error } = await supabase.from('vendors').insert(fields).select('id').single()
        if (error) throw error
        vendorId = data.id
      }
      const { error: clearError } = await supabase.from('vendor_jobs').delete().eq('vendor_id', vendorId)
      if (clearError) throw clearError
      if (jobIds.length) {
        const { error: linkError } = await supabase.from('vendor_jobs').insert(jobIds.map((job_id) => ({ vendor_id: vendorId, job_id })))
        if (linkError) throw linkError
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all })
      toast.success('Saved')
    },
    onError: (error) => toast.error('Could not save the vendor', { description: describeError(error) }),
  })
}

export function useDeleteVendor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('vendors').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: vendorKeys.all }),
    onError: (error) => toast.error('Could not delete the vendor', { description: describeError(error) }),
  })
}
